import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";

import { confirmActivation, confirmDeleteUser } from "config/confirmConfig";
import { ERROR_401 } from "config/errorPage";
import {
  toastDeleteErrorMessageConfig,
  toastDeleteSuccessMessageConfigUser,
  toastErrorMessageWithParameterConfig,
  toastSuccessMessageWithParameterConfig,
} from "config/toastConfig";
import { loadCss } from "helpers/function";
import { ToastBottomEnd } from "views/components/Toast";

import { DeleteConfirmation } from "views/components/ConfirmationAlert";
import EmptyTable from "views/components/EmptyTable";
import HeaderTablePage from "views/components/HeaderTablePage";
import LoadingTable from "views/components/LoadingTable";
import Pagination from "views/components/Pagination";
import UserSearch from "components/users/UserSearch";
import UserTableHeader from "components/users/UserTableHeader";
import UserTableRow from "components/users/UserTableRow";

import { useUsers } from "hooks/useUsers";
import { createEndpoints } from "services/userService";
import { verifyIfAnyPermission, verifyPermission } from "../../helpers/permission";
import FileSaver from "file-saver";
import axios from "axios";
import Select from "react-select";
import { verifyTokenExpire } from "middleware/verifyToken";

loadCss("/assets/plugins/custom/datatables/datatables.bundle.css");

const User = ({ userPermissions, isHolding, plan }) => {
  const { t, ready } = useTranslation();

  const hasListPermission = useMemo(() => {
    return (
      userPermissions &&
      (userPermissions.includes("list-user-any-institution") ||
        userPermissions.includes("list-user-my-institution"))
    );
  }, [userPermissions]);

  const endPoint = useMemo(() => {
    return createEndpoints(plan, userPermissions);
  }, [plan, userPermissions]);

  const {
    filteredUsers,
    entity,
    setEntity,
    entityOptions,
    loading,
    searchTerm,
    setSearchTerm,
    logoutUser,
    page,
    setPage,
    perPage,
    setPerPage,
    total,
    usersLogout,
    setUsers,
    enableDisableUser,
    downloadUsersPdf,
    downloadUsersExcel
  } = useUsers(endPoint);

  // Calculate pagination values based on server-side data
  const currentPage = page;
  const totalPages = Math.ceil(total / perPage) || 1;
  const displayedUsers = filteredUsers;

  const pageNumbers = useMemo(() => {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }, [totalPages]);
  
  const handleEntityChange = useCallback((selected) => {
    setEntity(selected);
  }, []);

  
  


  const formatUnitSelectOption = useCallback(
    (options, labelKey, translate, valueKey = "id") => {
        if (!options) return [];
        return options?.map((option) => ({
          value: option[valueKey],
          label: translate ? option[labelKey][translate] : option[labelKey].fr ? option[labelKey].fr : option[labelKey],
          lead: option.lead,
          key: isHolding ? 'institution_id' : 'unit_id'
        }));
      },
    []
  );

  const handleSearch = useCallback(
    (e) => {
      setSearchTerm(e.target.value);
    },
    [setSearchTerm]
  );

  const handleEnableDisableUser = useCallback(
    (e, userId, index, label, status) => {
      e.preventDefault();
      DeleteConfirmation.fire(confirmActivation(status)).then((result) => {
        if (result.value) {
          const spinnerElement = document.getElementById(
            `user-spinner-${userId}`
          );
          const userElement = document.getElementById(`user-${userId}`);
          const editElement = document.getElementById(`user-edit-${userId}`);

          if (spinnerElement) spinnerElement.style.display = "block";
          if (userElement) userElement.style.display = "none";
          if (editElement) editElement.style.display = "none";

          enableDisableUser(userId)
            .then((success) => {
              if (success) {
                const newFilteredUsers = [...filteredUsers];
                const idx = index;

                if (newFilteredUsers[idx]) {
                  newFilteredUsers[idx].disabled_at =
                    newFilteredUsers[idx].disabled_at === null ? true : null;

                  if (spinnerElement) spinnerElement.style.display = "none";
                  if (userElement) userElement.style.display = "block";
                  if (editElement) editElement.style.display = "block";

                  setUsers(newFilteredUsers);
                  ToastBottomEnd.fire(
                    toastSuccessMessageWithParameterConfig(
                      t("Succès de l'opération")
                    )
                  );
                }
              }
            })
            .catch((error) => {
              console.error("Error enabling/disabling user:", error);

              if (spinnerElement) spinnerElement.style.display = "none";
              if (userElement) userElement.style.display = "block";
              if (editElement) editElement.style.display = "block";

              if (error.response?.data?.error) {
                ToastBottomEnd.fire(
                  toastErrorMessageWithParameterConfig(
                    error.response.data.error
                  )
                );
              } else {
                ToastBottomEnd.fire(
                  toastErrorMessageWithParameterConfig(
                    t("Echec de l'opération")
                  )
                );
              }
            });
        }
      });
    },
    [enableDisableUser, filteredUsers, setUsers, t]
  );

  const handleLogoutUser = useCallback(
    (e, userId) => {
      e.preventDefault();
      DeleteConfirmation.fire(confirmDeleteUser()).then((result) => {
        if (result.value) {
          logoutUser(userId)
            .then((success) => {
              if (success) {
                ToastBottomEnd.fire(toastDeleteSuccessMessageConfigUser());
              }
            })
            .catch((error) => {
              console.error("Error logging out user:", error);
              if (error.response?.data?.error) {
                ToastBottomEnd.fire(
                  toastErrorMessageWithParameterConfig(
                    error.response.data.error
                  )
                );
              } else {
                ToastBottomEnd.fire(toastDeleteErrorMessageConfig());
              }
            });
        }
      });
    },
    [logoutUser]
  );

  const handlePageChange = useCallback(
    (e, page) => {
      e.preventDefault();
      setPage(page);
    },
    [setPage]
  );

  const handleNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      setPage(currentPage + 1);
    }
  }, [currentPage, totalPages, setPage]);

  const handlePrevPage = useCallback(() => {
    if (currentPage > 1) {
      setPage(currentPage - 1);
    }
  }, [currentPage, setPage]);

  const handleChangeItemsPerPage = useCallback((e) => {
    const value = parseInt(e.target.value);
    setPerPage(value);
  }, [setPerPage]);


  useEffect(() => {
    setEntity(
      formatUnitSelectOption(
        entityOptions,
        "name"
      )[0]
    )
  }, [entityOptions]);
  
  useEffect(() => {
    if (!hasListPermission && ready) {
      window.location.href = ERROR_401;
    }
  }, [hasListPermission, ready]);

  if (!hasListPermission || !ready) {
    return null;
  }


  return (
    <div
      className="kt-content kt-grid__item kt-grid__item--fluid kt-grid kt-grid--hor"
      id="kt_content"
    >
      <div className="kt-subheader kt-grid__item" id="kt_subheader">
        <div className="kt-container kt-container--fluid">
          <div className="kt-subheader__main">
            <h3 className="kt-subheader__title">{t("Paramètres")}</h3>
            <span className="kt-subheader__separator kt-hidden" />
            <div className="kt-subheader__breadcrumbs">
              <a href="#icone" className="kt-subheader__breadcrumbs-home">
                <i className="flaticon2-shelter" />
              </a>
              <span className="kt-subheader__breadcrumbs-separator" />
              <a
                href="#button"
                onClick={(e) => e.preventDefault()}
                className="kt-subheader__breadcrumbs-link"
                style={{ cursor: "text" }}
              >
                {t("Utilisateur")}
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="kt-container kt-container--fluid kt-grid__item kt-grid__item--fluid">
        <div className="kt-portlet">
          <HeaderTablePage
            addPermission={[
              "store-user-any-institution",
              "store-user-my-institution",
            ]}
            title={t("Utilisateur")}
            addText={verifyIfAnyPermission(userPermissions, ["store-user-any-institution", "store-user-my-institution"]) && t("Ajouter")}
            addLink={verifyIfAnyPermission(userPermissions, ["store-user-any-institution", "store-user-my-institution"]) && "/settings/users/add"}
            withDownloadPdfCallback={downloadUsersPdf}
            withDownloadExcelCallback={downloadUsersExcel}
            loading={loading}
          />

          {loading ? (
            <LoadingTable />
          ) : (
            <div className="kt-portlet__body">
              <div
                id="kt_table_1_wrapper"
                className="dataTables_wrapper dt-bootstrap4"
              >
                <div className="row">
                  <div className="col-sm-6 text-left">
                    <UserSearch
                      searchTerm={searchTerm}
                      onSearch={handleSearch}
                    />
                  </div>
                  <div className="col-sm-6">
                    <div className="row col">
                      <label
                        htmlFor="unit"
                        className="col-xl-2 col-lg-2 col-form-label"
                      >
                        {plan === "PRO"
                          ? t("Unité")
                          : t(
                              !isHolding
                                ? "Unité"
                                : "Institution"
                            )}
                      </label>
                      <div className="" style={{ width: "83%" }}>
                        <Select
                          isClearable
                          value={entity}
                          onChange={handleEntityChange}
                          placeholder={`${t("Filtrer en fonction de ")} ${t(
                            !isHolding
                              ? "l'unité"
                              : "l'institution"
                          )}`}
                          options={formatUnitSelectOption(
                            entityOptions,
                            "name"
                          )}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-sm-12">
                    <table
                      className="table table-striped table-bordered table-hover table-checkable dataTable dtr-inline"
                      id="myTable"
                      role="grid"
                      aria-describedby="kt_table_1_info"
                      style={{ width: "100%" }}
                    >
                      <UserTableHeader />
                      <tbody>
                        {filteredUsers.length > 0 ? (
                          displayedUsers.map((user, index) => (
                            <UserTableRow
                              key={user.id || index}
                              user={user}
                              index={index}
                              onLogoutUser={(e) => handleLogoutUser(e, user.id)}
                              onEnableDisable={handleEnableDisableUser}
                              userLogout={usersLogout.includes(user.id)}
                            />
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5">
                              <EmptyTable search={searchTerm.length > 0} />
                            </td>
                          </tr>
                        )}
                      </tbody>
                      <tfoot>
                        <tr>
                          <th>{t("Nom")}</th>
                          <th>{t("Email")}</th>
                          <th>{t("Rôle")}</th>
                          <th>{t("Statut")}</th>
                          <th>{t("Actions")}</th>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                <div className="row">
                  <div className="col-sm-12 col-md-5">
                    <div
                      className="dataTables_info"
                      id="kt_table_1_info"
                      role="status"
                      aria-live="polite"
                    >
                      {total > 0
                        ? `${t("Affichage de")} ${filteredUsers.length} ${t("sur")} ${total} ${t("données")}`
                        : t("Affichage de 0 données")}
                    </div>
                  </div>

                  {displayedUsers.length > 0 && (
                    <div className="col-sm-12 col-md-7 dataTables_pager">
                      <Pagination
                        numberPerPage={perPage}
                        onChangeNumberPerPage={handleChangeItemsPerPage}
                        activeNumberPage={currentPage}
                        onClickPreviousPage={handlePrevPage}
                        pages={pageNumbers}
                        onClickPage={handlePageChange}
                        numberPage={totalPages}
                        onClickNextPage={handleNextPage}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    userPermissions: state.user.user.permissions,
    isHolding: state?.user?.user?.institution?.institution_type?.name === "holding",
    plan: state.plan.plan,
  };
};

export default connect(mapStateToProps)(User);
